import { api, API_ENDPOINTS } from '../lib/api';
import { Vehicle, CreateVehicleRequest, UpdateVehicleRequest } from '../types';

/**
 * Vehicle Service
 * Handles vehicle CRUD operations and primary vehicle management
 */
export const vehicleService = {
  /**
   * Get all vehicles for the logged-in user
   * @returns Array of user's vehicles
   */
  getMyVehicles: async (): Promise<Vehicle[]> => {
    const response = await api.get<Vehicle[]>(API_ENDPOINTS.VEHICLES.MY_VEHICLES);
    return Array.isArray(response.data) ? response.data : [];
  },

  /**
   * Get vehicle by ID
   * @param vehicleId - Vehicle UUID
   * @returns Vehicle details
   */
  getVehicleById: async (vehicleId: string): Promise<Vehicle> => {
    const response = await api.get<Vehicle>(API_ENDPOINTS.VEHICLES.BY_ID(vehicleId));
    return response.data;
  },

  /**
   * Create a new vehicle
   * @param data - Vehicle details including make, model, year, connector types, etc.
   * @returns Created vehicle
   */
  createVehicle: async (data: CreateVehicleRequest): Promise<Vehicle> => {
    const response = await api.post<Vehicle>(API_ENDPOINTS.VEHICLES.BASE, data);
    return response.data;
  },

  /**
   * Update an existing vehicle
   * @param vehicleId - Vehicle UUID
   * @param data - Partial vehicle data to update
   * @returns Updated vehicle
   */
  updateVehicle: async (vehicleId: string, data: UpdateVehicleRequest): Promise<Vehicle> => {
    const response = await api.patch<Vehicle>(API_ENDPOINTS.VEHICLES.BY_ID(vehicleId), data);
    return response.data;
  },

  /**
   * Delete a vehicle
   * @param vehicleId - Vehicle UUID
   */
  deleteVehicle: async (vehicleId: string): Promise<void> => {
    await api.delete(API_ENDPOINTS.VEHICLES.BY_ID(vehicleId));
  },

  /**
   * Set a vehicle as the primary vehicle
   * Only one vehicle can be primary at a time
   * @param vehicleId - Vehicle UUID to set as primary
   * @returns Updated vehicle with isPrimary = true
   */
  setPrimaryVehicle: async (vehicleId: string): Promise<Vehicle> => {
    const response = await api.patch<Vehicle>(API_ENDPOINTS.VEHICLES.SET_PRIMARY(vehicleId));
    return response.data;
  },

  /**
   * Get vehicle reference data (connector types and vehicle templates)
   * @returns Reference data including connector types and vehicle templates
   */
  getReferenceData: async (): Promise<{
    connectorTypes: Array<{
      id: string;
      name: string;
      description?: string;
      standard?: string;
      maxPowerKw?: number;
      isAC?: boolean;
      isDC?: boolean;
    }>;
    vehicleTemplates: Array<{
      make: string;
      model: string;
      years: number[];
      connectorTypes: string[];
      maxChargingSpeedKw: number;
      vehicleType?: '2wheeler' | '3wheeler' | '4wheeler';
    }>;
  }> => {
    const response = await api.get(API_ENDPOINTS.VEHICLES.REFERENCE_DATA);
    return response.data;
  },
};
